import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { formatReportDate } from '../../reports/formatting';
import { jobRoleGateway } from '../index';
import { jobRoleErrorMessage } from '../jobRoleErrors';
import {
  jobRoleVersionStatusLabels, type JobRoleAuditItem, type JobRoleDetail, type JobRoleEmployee, type JobRoleVersionDetail, type JobRoleVersionItem, type Paged,
} from '../types';
import { JobDescription } from './JobDescription';
import { formatDateTime, useTabLoad } from './tabLoad';

const PAGE_SIZE = 10;

const dash = <span className="muted">{'—'}</span>;

function Pager({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <nav className="jr-pager__nav" aria-label="Pagination">
      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span>Page {page} of {totalPages}</span>
      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next</button>
    </nav>
  );
}

/** Names and employee numbers only. The contract keeps pay and every other sensitive field out of this list. */
export function AssignedEmployeesTab({ roleId }: { roleId: string }) {
  const [page, setPage] = useState(1);
  const { state, retry } = useTabLoad<Paged<JobRoleEmployee>>((signal) => jobRoleGateway.listRoleEmployees(roleId, { page, pageSize: PAGE_SIZE }, signal), [roleId, page], 'Could not load the assigned employees.');
  if (state.kind === 'loading') return <p className="muted" role="status">Loading assigned employees{'…'}</p>;
  if (state.kind === 'forbidden') return <AccessDeniedNotice requiredPermissionLabel="permission to view roles and employees (roles.read and employees.read)" />;
  if (state.kind === 'error') return <RetryableError message={state.message} onRetry={retry} />;
  const { items, totalCount } = state.data;
  if (totalCount === 0) return <p className="muted">No employees currently have this role.</p>;
  return (
    <>
      <p className="muted">Only names and employee numbers are shown here.</p>
      <div className="jr-table-scroll" role="region" aria-label="Assigned employees table" tabIndex={0}>
        <table className="jr-table">
          <caption className="jr-sr-only">Employees assigned to this role</caption>
          <thead><tr><th scope="col">Employee</th><th scope="col">Employee number</th><th scope="col">In this role since</th></tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.employeeId}>
                <th scope="row"><Link to={`/people/${e.employeeId}/employee-setup`}>{e.displayName}</Link></th>
                <td>{e.employeeNumber}</td>
                <td>{e.effectiveFrom ? formatReportDate(e.effectiveFrom) : dash}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={state.data.page} totalPages={Math.max(1, Math.ceil(totalCount / state.data.pageSize))} onPage={setPage} />
    </>
  );
}

export function VersionHistoryTab({ role }: { role: JobRoleDetail }) {
  const { state, retry } = useTabLoad<JobRoleVersionItem[]>((signal) => jobRoleGateway.listVersions(role.id, signal), [role.id, role.concurrencyVersion], 'Could not load the version history.');
  const [open, setOpen] = useState<number | null>(null);
  const [detail, setDetail] = useState<Record<number, JobRoleVersionDetail>>({});
  const [detailError, setDetailError] = useState<string | null>(null);

  async function toggle(versionNumber: number) {
    if (open === versionNumber) {
      setOpen(null);
      return;
    }
    setOpen(versionNumber);
    setDetailError(null);
    if (detail[versionNumber]) return;
    try {
      const loaded = await jobRoleGateway.getVersion(role.id, versionNumber);
      setDetail((prev) => ({ ...prev, [versionNumber]: loaded }));
    } catch (err) {
      setDetailError(jobRoleErrorMessage(err, 'Could not load that version.'));
    }
  }

  if (state.kind === 'loading') return <p className="muted" role="status">Loading version history{'…'}</p>;
  if (state.kind === 'forbidden') return <AccessDeniedNotice requiredPermissionLabel="permission to view roles (roles.read)" />;
  if (state.kind === 'error') return <RetryableError message={state.message} onRetry={retry} />;
  if (state.data.length === 0) {
    return <p className="muted">This role has no versions yet. It was created before the Job Role Library, so it has only a title and department. Create a new version to describe it.</p>;
  }
  const opened = open === null ? undefined : detail[open];
  return (
    <>
      <p className="muted">A published version is never changed. Assignments keep pointing at the version that applied when they were made.</p>
      <div className="jr-table-scroll" role="region" aria-label="Version history table" tabIndex={0}>
        <table className="jr-table">
          <caption className="jr-sr-only">Versions of this role, newest first</caption>
          <thead>
            <tr><th scope="col">Version</th><th scope="col">Status</th><th scope="col">Effective from</th><th scope="col">Effective to</th><th scope="col">Created</th><th scope="col">Published</th><th scope="col">Description</th></tr>
          </thead>
          <tbody>
            {state.data.map((v) => (
              <tr key={v.versionNumber}>
                <th scope="row">Version {v.versionNumber}</th>
                <td>{jobRoleVersionStatusLabels[v.status] ?? 'Unknown'}</td>
                <td>{v.effectiveFromUtc ? formatReportDate(v.effectiveFromUtc) : dash}</td>
                <td>{v.effectiveToUtc ? formatReportDate(v.effectiveToUtc) : dash}</td>
                <td>{formatDateTime(v.createdAtUtc)}</td>
                <td>{v.publishedAtUtc ? formatDateTime(v.publishedAtUtc) : dash}</td>
                <td>
                  <button type="button" className="link" aria-expanded={open === v.versionNumber} onClick={() => void toggle(v.versionNumber)}>
                    {open === v.versionNumber ? 'Hide' : 'View'}<span className="jr-sr-only"> version {v.versionNumber}</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open !== null && (
        <div className="jr-version-view" role="region" aria-label={`Version ${open} description`}>
          {detailError && <p className="jr-error" role="alert">{detailError}</p>}
          {!opened && !detailError && <p className="muted" role="status">Loading version {open}{'…'}</p>}
          {opened && <JobDescription header={{ title: role.title, roleCode: role.roleCode, departmentName: role.departmentName, jobFamily: role.jobFamily, careerLevel: role.careerLevel, managementLevel: role.managementLevel, socCode: role.socCode, onetCode: role.onetCode }} content={opened.content} />}
        </div>
      )}
    </>
  );
}

const EVENT_LABELS: Record<string, string> = {
  JobRoleCreated: 'Role created',
  JobRoleCopiedFromTemplate: 'Created from an EPN template',
  JobRoleDuplicated: 'Role duplicated',
  JobRoleUpdated: 'Draft updated',
  JobRoleVersionCreated: 'New version started',
  JobRoleActivated: 'Role activated',
  JobRoleArchived: 'Role archived',
  JobRoleRestored: 'Role restored',
  JobRolePayBandSet: 'Employer pay band set',
  JobRolePayBandChanged: 'Employer pay band changed',
  JobRolePayBandEnded: 'Employer pay band ended',
  EmployeeJobRoleAssigned: 'Employee assigned to this role',
  EmployeeJobRoleChanged: 'Employee moved to or from this role',
};

function eventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** "ManagementLevel" reads as "Management level". */
function fieldLabel(name: string): string {
  const spaced = name.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** Metadata is ids and changed field NAMES only (never values), so showing the names is safe. */
function changedFields(metadataJson: string | null): string[] {
  if (!metadataJson) return [];
  try {
    const parsed: unknown = JSON.parse(metadataJson);
    const fields = typeof parsed === 'object' && parsed !== null ? (parsed as { changedFields?: unknown }).changedFields : undefined;
    return Array.isArray(fields) ? fields.filter((f): f is string => typeof f === 'string') : [];
  } catch {
    return [];
  }
}

export function AuditHistoryTab({ roleId }: { roleId: string }) {
  const [page, setPage] = useState(1);
  const { state, retry } = useTabLoad<Paged<JobRoleAuditItem>>((signal) => jobRoleGateway.listRoleAudit(roleId, { page, pageSize: PAGE_SIZE }, signal), [roleId, page], 'Could not load the audit history.');
  if (state.kind === 'loading') return <p className="muted" role="status">Loading audit history{'…'}</p>;
  if (state.kind === 'forbidden') return <AccessDeniedNotice requiredPermissionLabel="permission to view roles (roles.read)" />;
  if (state.kind === 'error') return <RetryableError message={state.message} onRetry={retry} />;
  const { items, totalCount } = state.data;
  if (totalCount === 0) return <p className="muted">No audit events have been recorded for this role yet.</p>;
  return (
    <>
      <div className="jr-table-scroll" role="region" aria-label="Audit history table" tabIndex={0}>
        <table className="jr-table">
          <caption className="jr-sr-only">Audit events for this role, newest first</caption>
          <thead><tr><th scope="col">When</th><th scope="col">Event</th><th scope="col">By</th><th scope="col">Fields changed</th></tr></thead>
          <tbody>
            {items.map((e) => {
              const fields = changedFields(e.metadataJson);
              return (
                <tr key={e.id}>
                  <td>{formatDateTime(e.occurredAtUtc)}</td>
                  <th scope="row">{eventLabel(e.eventType)}</th>
                  <td>{e.actorEmailSnapshot ?? 'Not recorded'}</td>
                  <td>{fields.length > 0 ? fields.map(fieldLabel).join(', ') : dash}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager page={state.data.page} totalPages={Math.max(1, Math.ceil(totalCount / state.data.pageSize))} onPage={setPage} />
    </>
  );
}

